import React, { useEffect, useState } from "react";
import { Globe, CheckCircle, Warning, Spinner, Copy, ArrowRight, EnvelopeSimple, TrashSimple } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const DNS_STEPS = [
  { title: "Create a free Resend account", body: "Go to resend.com and sign up. Free tier: 3,000 emails/month." },
  { title: "Add your domain", body: "In Resend → Domains → Add domain. Enter yourdomain.com (root domain, no www). Choose region closest to your customers." },
  { title: "Copy the DNS records Resend shows you", body: "Resend gives you 3 records: SPF (TXT), DKIM (TXT), Return-Path (CNAME). Keep that page open." },
  { title: "Add those records to your DNS provider", body: "GoDaddy / Cloudflare / Namecheap: paste each record. Set TTL to 1 hour. Save." },
  { title: "Wait for Resend to verify (~10 min)", body: "Resend will show green checkmarks when DNS propagates. If your registrar is slow, this can take up to 24 hrs." },
  { title: "Create an API key in Resend", body: "In Resend → API Keys → Create → Full access. Copy the key starting with re_..." },
  { title: "Paste your API key + sender email below", body: "We'll send a real test email to verify. Once verified, all your outreach sends from your domain." },
];

export default function CustomSenderSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("managed"); // managed | shared
  const [apiKey, setApiKey] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/custom-sender/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({}))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const submitManaged = async () => {
    if (!apiKey.trim().startsWith("re_")) {
      toast.error("Resend keys start with 're_'");
      return;
    }
    if (!senderEmail.includes("@")) {
      toast.error("Enter a valid sender email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/custom-sender/managed/submit", {
        resend_api_key: apiKey.trim(),
        sender_email: senderEmail.trim().toLowerCase(),
      });
      toast.success(data.message);
      setApiKey("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 402) {
        toast.error("Custom sender is a Pro-only feature. Upgrade to unlock.");
      } else {
        toast.error(formatApiErrorDetail(detail) || "Verification failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitShared = async () => {
    if (!senderEmail.includes("@")) {
      toast.error("Enter a valid sender email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/custom-sender/shared/request", {
        desired_sender_email: senderEmail.trim().toLowerCase(),
        reason,
      });
      toast.success(data.message, { duration: 8000 });
      setSenderEmail("");
      setReason("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const st = e?.response?.status;
      if (st === 402) {
        toast.error("Custom sender is a Pro-only feature. Upgrade to unlock.");
      } else {
        toast.error(formatApiErrorDetail(detail) || "Request failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your custom sender and revert to the default info@clickforgeds.com?")) return;
    setBusy(true);
    try {
      await api.delete("/custom-sender/");
      toast.success("Custom sender removed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-6 text-center">
        <Spinner size={16} className="animate-spin mx-auto text-zinc-500" />
      </div>
    );
  }

  // Already configured / verified
  if (status.verified && status.sender_email) {
    return (
      <div
        data-testid="custom-sender-section"
        className="bg-zinc-900/60 border border-emerald-500/40 rounded-sm p-5"
      >
        <div className="flex items-center gap-2 mb-3">
          <Globe size={16} weight="duotone" className="text-emerald-400" />
          <div>
            <div className="font-display font-medium text-zinc-100">Custom Sender</div>
            <div className="text-[11px] font-mono text-zinc-500">Sending from your own domain</div>
          </div>
          <div className="ml-auto flex items-center gap-1 text-emerald-400">
            <CheckCircle size={14} weight="fill" />
            <span className="text-[10px] font-mono uppercase tracking-wider">Verified</span>
          </div>
        </div>
        <div className="bg-zinc-950/40 border border-zinc-800 rounded-sm p-3 mb-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Active sender</div>
          <div className="flex items-center gap-2 mt-1">
            <EnvelopeSimple size={13} className="text-emerald-400" />
            <span className="text-sm font-mono text-zinc-100" data-testid="custom-sender-active">{status.sender_email}</span>
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 ml-auto">{status.mode} mode</span>
          </div>
        </div>
        <Button
          onClick={remove}
          disabled={busy}
          variant="outline"
          data-testid="custom-sender-remove"
          className="w-full border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200"
        >
          <TrashSimple size={13} className="mr-2" /> Revert to default sender
        </Button>
      </div>
    );
  }

  // Shared path — pending approval
  if (status.mode === "shared" && !status.verified && status.sender_email) {
    return (
      <div className="bg-zinc-900/60 border border-amber-500/40 rounded-sm p-5" data-testid="custom-sender-pending">
        <div className="flex items-center gap-2 mb-3">
          <Warning size={16} weight="fill" className="text-amber-400" />
          <div>
            <div className="font-display font-medium text-zinc-100">Awaiting admin approval</div>
            <div className="text-[11px] font-mono text-zinc-500">Requested: <span className="text-amber-300">{status.sender_email}</span></div>
          </div>
        </div>
        <div className="text-[13px] text-zinc-400 leading-relaxed mb-3">
          We'll email you once your domain is added to our Resend account and approved. Usually within 24 hours.
        </div>
        <Button onClick={remove} variant="outline" size="sm" className="border-zinc-700">
          Cancel request
        </Button>
      </div>
    );
  }

  // Not configured — show the two-path chooser
  return (
    <div
      data-testid="custom-sender-section"
      className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5"
    >
      <div className="flex items-center gap-2 mb-1">
        <Globe size={16} weight="duotone" className="text-orange-400" />
        <div className="font-display font-medium text-zinc-100">Custom Sender Domain</div>
        <span className="text-[9px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-sm bg-orange-500/15 text-orange-300 border border-orange-500/30 ml-1">Pro</span>
      </div>
      <div className="text-[12px] text-zinc-400 leading-relaxed mb-4">
        Send outreach from <span className="font-mono text-orange-300">you@yourdomain.com</span> instead of our shared address. Two ways to set up:
      </div>

      {/* Mode tabs */}
      <div className="flex gap-1 mb-4 bg-zinc-950/60 border border-zinc-800 rounded-sm p-1">
        <button
          onClick={() => setMode("managed")}
          data-testid="mode-managed-tab"
          className={`flex-1 px-3 py-1.5 text-[12px] font-mono uppercase tracking-wider rounded-sm transition-colors ${
            mode === "managed" ? "bg-orange-500/20 text-orange-200" : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Bring your own Resend
        </button>
        <button
          onClick={() => setMode("shared")}
          data-testid="mode-shared-tab"
          className={`flex-1 px-3 py-1.5 text-[12px] font-mono uppercase tracking-wider rounded-sm transition-colors ${
            mode === "shared" ? "bg-orange-500/20 text-orange-200" : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Request admin approval
        </button>
      </div>

      {mode === "managed" ? (
        <div className="space-y-4">
          <div className="bg-zinc-950/40 border border-zinc-800 rounded-sm p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-orange-500 mb-2">Setup guide</div>
            <ol className="space-y-2 text-[12px] text-zinc-400">
              {DNS_STEPS.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-orange-400 font-mono shrink-0">{i + 1}.</span>
                  <div>
                    <div className="text-zinc-200">{s.title}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{s.body}</div>
                  </div>
                </li>
              ))}
            </ol>
            <a
              href="https://resend.com/domains"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-mono text-orange-400 hover:text-orange-300 mt-3"
            >
              Open Resend Domains <ArrowRight size={11} weight="bold" />
            </a>
          </div>

          <div className="space-y-3 pt-2 border-t border-zinc-800/60">
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                Your Resend API key
              </Label>
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                data-testid="managed-apikey-input"
                className="bg-zinc-900 border-zinc-700 font-mono text-[12px]"
              />
              <div className="text-[10px] text-zinc-600 mt-1">Stored encrypted. Only used to send your outreach.</div>
            </div>
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                Sender email (must be on a verified Resend domain)
              </Label>
              <Input
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="you@yourdomain.com"
                data-testid="managed-sender-input"
                className="bg-zinc-900 border-zinc-700"
              />
            </div>
            <Button
              onClick={submitManaged}
              disabled={busy}
              data-testid="managed-submit"
              className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium"
            >
              {busy ? <><Spinner size={14} className="animate-spin mr-2" /> Verifying…</> : "Verify & activate"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-zinc-950/40 border border-zinc-800 rounded-sm p-3 text-[12px] text-zinc-400 leading-relaxed">
            <strong className="text-zinc-200">How it works:</strong> submit the sender email you want. Michael manually adds your domain to our shared Resend account. Your outreach sends from your domain (no Resend account or API key needed on your side).
          </div>
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
              Sender email you want
            </Label>
            <Input
              type="email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="hello@yourbrand.com"
              data-testid="shared-sender-input"
              className="bg-zinc-900 border-zinc-700"
            />
          </div>
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
              Anything we should know (optional)
            </Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Company details, expected send volume, urgency"
              rows={2}
              maxLength={500}
              data-testid="shared-reason-input"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-orange-500 focus:outline-none resize-y"
            />
          </div>
          <Button
            onClick={submitShared}
            disabled={busy}
            data-testid="shared-submit"
            className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium"
          >
            {busy ? <><Spinner size={14} className="animate-spin mr-2" /> Submitting…</> : "Submit request"}
          </Button>
        </div>
      )}
    </div>
  );
}
